import React, { useCallback, useEffect, useRef, useState } from "react";
import { useScannerContext } from "@/features/scanners/ScannerContext";
import { BarcodeDataType } from "@/features/scanners/types";
import { appLog, barcodeDataTypeFromQrCode, beepOk, beepWarning, hasQRcodeNumberPattern } from "@/features/scanners/helpers";
import QrCodeInputField from "@/features/scanners/QrCodeInputField";
import { showManualQrCodeInput } from "@/constants";
import { completeWorktableScan, scanWorktable } from "./api";
import {
  WORKTABLE_ACTION_NOT_OK,
  WORKTABLE_ACTION_OK,
  WorktableCompleteResponse,
  WorktableProduct,
  WorktableScanResponse,
  WorktableStats,
} from "./types";

const RESULT_RESET_MS = 6000;

const messages = {
  scan_first: "Scan eerst een product of zak",
  invalid_qrcode: "Geen geldige QR code voor de werktafel",
  scan_failed: "Product of zak kon niet geladen worden",
  action_failed: "Actie is mislukt, probeer opnieuw",
  bag_already_processed: "Alle producten in deze zak zijn al verwerkt",
};

// Map known API error codes to a clear Dutch message for the worker
const apiErrorMessages: Record<string, string> = {
  NO_OPEN_PRODUCTS_IN_BAG: messages.bag_already_processed,
  ORDER_BAG_NOT_FOUND: "Zak niet gevonden",
  ORDER_PRODUCT_NOT_FOUND: "Product niet gevonden",
  NO_ORDER_BAG_IN_QR_CODE: "Dit is geen QR code van een zak",
  NO_PRODUCT_OR_BAG_IN_QR_CODE: "Dit is geen QR code van een zak of product",
  "Worktable Not Found": "Werktafel niet gevonden",
};

const translateApiError = (error: Error, fallback: string): string =>
  apiErrorMessages[error.message] || fallback;

const actionQrImageUrl = (data: string) => {
  const params = new URLSearchParams({ size: "220x220", qzone: "2", data });
  return `https://api.qrserver.com/v1/create-qr-code/?${params.toString()}`;
};

const parseEntityIdFromDataString = (dataString: string): number | null => {
  const parts = dataString.trim().split(/\s+/);
  const id = Number(parts[parts.length - 1]);
  return Number.isFinite(id) ? id : null;
};

interface ScanProps {
  worktableId: number;
  onStatsChange?: (stats: WorktableStats) => void;
}

export const Scan: React.FC<ScanProps> = ({ worktableId, onStatsChange }) => {
  const { scannedData, setScannedData, clearScannedData, enableScanner } = useScannerContext();
  const [bagScan, setBagScan] = useState<WorktableScanResponse | null>(null);
  const [bagDataString, setBagDataString] = useState<string | null>(null);
  const [focusedProduct, setFocusedProduct] = useState<WorktableProduct | null>(null);
  const [focusedProductDataString, setFocusedProductDataString] = useState<string | null>(null);
  const [result, setResult] = useState<WorktableCompleteResponse | null>(null);
  const [message, setMessage] = useState<string>("");
  const [busy, setBusy] = useState(false);
  const resetTimerRef = useRef<number | null>(null);

  const clearResetTimer = () => {
    if (resetTimerRef.current) {
      window.clearTimeout(resetTimerRef.current);
      resetTimerRef.current = null;
    }
  };

  const resetToScanState = useCallback(() => {
    clearResetTimer();
    setBagScan(null);
    setBagDataString(null);
    setFocusedProduct(null);
    setFocusedProductDataString(null);
    setResult(null);
    setMessage("");
  }, []);

  useEffect(() => {
    void enableScanner(true);
  }, [enableScanner]);

  useEffect(() => {
    return () => {
      clearResetTimer();
      clearScannedData();
    };
  }, [clearScannedData]);

  const handleScan = useCallback(
    async (dataString: string) => {
      setBusy(true);
      setMessage("");
      try {
        const response = await scanWorktable(worktableId, dataString);
        clearResetTimer();
        setResult(null);
        setBagScan(response);
        if (response.scanType === "PRODUCT") {
          setBagDataString(null);
          setFocusedProduct(response.products[0] || null);
          setFocusedProductDataString(dataString);
        } else {
          setBagDataString(dataString);
          setFocusedProduct(null);
          setFocusedProductDataString(null);
        }
        onStatsChange?.(response.stats);
        beepOk();
      } catch (err) {
        beepWarning();
        setMessage(translateApiError(err as Error, messages.scan_failed));
        void appLog(`[WORKTABLE] scan failed: ${(err as Error).message}`, true, "error", err as Error);
      } finally {
        setBusy(false);
      }
    },
    [onStatsChange, worktableId]
  );

  const handleProductScan = useCallback(
    (dataString: string) => {
      // Product belongs to the currently loaded bag: focus it locally
      const productId = parseEntityIdFromDataString(dataString);
      const productInBag = bagScan?.scanType === "BAG" ? bagScan.products.find((p) => p.id === productId) : undefined;
      if (productInBag) {
        setMessage("");
        setFocusedProduct(productInBag);
        setFocusedProductDataString(dataString);
        beepOk();
        return;
      }
      // Otherwise load the product directly — scanning per product is the primary flow
      void handleScan(dataString);
    },
    [bagScan, handleScan]
  );

  const handleAction = useCallback(
    async (outcome: "OK" | "NOT_OK") => {
      const dataString = focusedProductDataString || bagDataString;
      if (!dataString) {
        setMessage(messages.scan_first);
        beepWarning();
        return;
      }
      setBusy(true);
      setMessage("");
      try {
        const response = await completeWorktableScan(worktableId, dataString, outcome);
        setResult(response);
        setBagScan(null);
        setBagDataString(null);
        setFocusedProduct(null);
        setFocusedProductDataString(null);
        onStatsChange?.(response.stats);
        if (outcome === "OK") {
          beepOk();
        } else {
          beepWarning();
        }
        clearResetTimer();
        resetTimerRef.current = window.setTimeout(() => {
          resetToScanState();
        }, RESULT_RESET_MS);
      } catch (err) {
        beepWarning();
        setMessage(translateApiError(err as Error, messages.action_failed));
        void appLog(`[WORKTABLE] complete failed: ${(err as Error).message}`, true, "error", err as Error);
      } finally {
        setBusy(false);
      }
    },
    [bagDataString, focusedProductDataString, onStatsChange, resetToScanState, worktableId]
  );

  useEffect(() => {
    if (!scannedData || busy) {
      return;
    }
    const data = scannedData.trim();
    clearScannedData();

    if (data === WORKTABLE_ACTION_OK) {
      void handleAction("OK");
      return;
    }
    if (data === WORKTABLE_ACTION_NOT_OK) {
      void handleAction("NOT_OK");
      return;
    }

    if (hasQRcodeNumberPattern(data)) {
      const dataLabelType = barcodeDataTypeFromQrCode(data);
      if (dataLabelType === BarcodeDataType.ORDER_BAG) {
        void handleScan(data);
        return;
      }
      if (dataLabelType === BarcodeDataType.ORDER_PRODUCT) {
        handleProductScan(data);
        return;
      }
    }

    setMessage(messages.invalid_qrcode);
    void appLog("[WORKTABLE] invalid scan input", true, "info", { dataString: data });
  }, [busy, clearScannedData, handleAction, handleScan, handleProductScan, scannedData]);

  const productsInView = focusedProduct ? [focusedProduct] : bagScan?.products || [];
  const completedStatuses = ["READY", "NOT_REPAIRED"];
  const completedStatusLabels: Record<string, string> = {
    READY: "Gereed",
    NOT_REPAIRED: "Niet hersteld",
  };
  const bagFullyProcessed =
    !!bagScan && bagScan.products.length > 0 && bagScan.products.every((p) => completedStatuses.includes(p.status));
  const showActionButtons = !!focusedProduct || !bagFullyProcessed;

  return (
    <div className="flex flex-col gap-2">
      {/* Result screen */}
      {result && (
        <button
          type="button"
          className={`flex min-h-72 flex-col items-center justify-center rounded-md p-6 text-white ${
            result.code === "PRODUCTS_READY" ? "bg-green-400" : "bg-red-500"
          }`}
          onClick={resetToScanState}
        >
          <div className="text-8xl font-bold">{result.code === "PRODUCTS_READY" ? "✓" : "✗"}</div>
          <div className="mt-2 text-3xl font-semibold">
            {result.code === "PRODUCTS_READY" ? "GEREED" : "NIET HERSTELD"}
          </div>
          <div className="mt-4 flex flex-col items-center gap-1 text-xl">
            {result.products.map((product) => (
              <div key={product.id}>
                {product.sequence ? `${product.sequence}. ` : ""}
                {product.name}
              </div>
            ))}
          </div>
          <div className="mt-4 text-base">Herstelling: {result.order.friendlyId}</div>
          <div className="mt-2 text-sm opacity-80">Scan het volgende product of tik om verder te gaan</div>
        </button>
      )}

      {/* Scan prompt */}
      {!result && !bagScan && (
        <div className="flex min-h-72 flex-col items-center justify-center rounded-md bg-gray-400 p-6 text-2xl font-semibold text-white">
          Scan een product (of een zak)
        </div>
      )}

      {/* Bag content + action QR codes */}
      {!result && bagScan && (
        <>
          <div className="rounded-md bg-white p-4 shadow-sm">
            <div className="flex items-baseline justify-between">
              <div className="text-2xl font-bold">
                {bagScan.scanType === "PRODUCT" ? "Product" : `Zak ${bagScan.orderBag?.sequence ?? ""}`}
              </div>
              {bagScan.order && <div className="text-base">Herstelling: {bagScan.order.friendlyId}</div>}
            </div>
            <div className="mt-3 flex flex-col gap-2">
              {productsInView.map((product) => (
                <div
                  key={product.id}
                  className={`flex items-center justify-between gap-2 rounded px-3 py-3 text-xl font-semibold ${
                    focusedProduct?.id === product.id ? "bg-blue-400 text-white" : "bg-blue-100 text-blue-900"
                  }`}
                >
                  <span>
                    {product.sequence ? `${product.sequence}. ` : ""}
                    {product.name}
                  </span>
                  {completedStatusLabels[product.status] && (
                    <span
                      className={`rounded px-2 py-1 text-sm text-white ${
                        product.status === "READY" ? "bg-green-400" : "bg-red-500"
                      }`}
                    >
                      {completedStatusLabels[product.status]}
                    </span>
                  )}
                </div>
              ))}
            </div>
            {bagFullyProcessed && !focusedProduct ? (
              <div className="mt-3 rounded bg-blue-100 px-3 py-3 text-center text-lg font-semibold text-blue-900">
                {messages.bag_already_processed}
              </div>
            ) : (
              <div className="mt-3 text-center text-sm text-blue-900">
                {focusedProduct
                  ? "Beoordeel dit product: scan GOED of NIET GOED"
                  : "Beoordeel de hele zak: scan GOED of NIET GOED. Of scan eerst één product."}
              </div>
            )}
            {focusedProduct && bagScan.scanType === "BAG" && bagScan.products.length > 1 && (
              <button
                type="button"
                className="mt-2 w-full rounded bg-gray-500 py-2 text-white"
                onClick={() => {
                  setFocusedProduct(null);
                  setFocusedProductDataString(null);
                }}
              >
                Terug naar hele zak
              </button>
            )}
          </div>

          {showActionButtons && (
          <div className="grid grid-cols-2 gap-2">
            <button
              type="button"
              className="flex flex-col items-center justify-center gap-2 rounded-md bg-green-400 p-4 text-white"
              onClick={() => void handleAction("OK")}
              disabled={busy}
            >
              <div className="text-6xl font-bold">✓</div>
              <div className="text-2xl font-bold">GOED</div>
              <img
                src={actionQrImageUrl(WORKTABLE_ACTION_OK)}
                alt="QR code GOED"
                className="h-40 w-40 rounded bg-white p-2"
                height={160}
                width={160}
              />
            </button>
            <button
              type="button"
              className="flex flex-col items-center justify-center gap-2 rounded-md bg-red-500 p-4 text-white"
              onClick={() => void handleAction("NOT_OK")}
              disabled={busy}
            >
              <div className="text-6xl font-bold">✗</div>
              <div className="text-2xl font-bold">NIET GOED</div>
              <img
                src={actionQrImageUrl(WORKTABLE_ACTION_NOT_OK)}
                alt="QR code NIET GOED"
                className="h-40 w-40 rounded bg-white p-2"
                height={160}
                width={160}
              />
            </button>
          </div>
          )}
        </>
      )}

      {message && (
        <div className="rounded-md bg-red-500 px-4 py-3 text-center text-xl font-semibold text-white">{message}</div>
      )}

      {showManualQrCodeInput && (
        <QrCodeInputField
          onChange={(value: string) => {
            setScannedData(value);
          }}
        />
      )}
    </div>
  );
};

export default Scan;
